/**
 * Authentication Slice
 * Manages authentication state, login, logout, and token refresh
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AUTH_CONFIG } from '../../constants/config';
import authService from '../../services/authService';

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password, rememberMe = false }, { rejectWithValue }) => {
    try {
      const response = await authService.login({ email, password });
      
      // Always store tokens and user data in localStorage for session persistence
      localStorage.setItem(AUTH_CONFIG.tokenKey, response.token);
      localStorage.setItem(AUTH_CONFIG.refreshTokenKey, response.refreshToken);
      localStorage.setItem(AUTH_CONFIG.userDataKey, JSON.stringify(response.user));
      
      // Store remember me preference and set expiration
      if (rememberMe) {
        localStorage.setItem(AUTH_CONFIG.rememberMeKey, 'true');
        // Set expiration timestamp for remember me sessions
        const expiryTime = Date.now() + AUTH_CONFIG.rememberMeExpiry;
        localStorage.setItem(AUTH_CONFIG.tokenKey + '_expiry', expiryTime.toString());
      } else {
        // Clear remember me preference but keep session data
        localStorage.removeItem(AUTH_CONFIG.rememberMeKey);
        localStorage.removeItem(AUTH_CONFIG.tokenKey + '_expiry');
      }
      
      return { ...response, rememberMe };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      // Clear all auth-related items from localStorage
      localStorage.removeItem(AUTH_CONFIG.tokenKey);
      localStorage.removeItem(AUTH_CONFIG.refreshTokenKey);
      localStorage.removeItem(AUTH_CONFIG.rememberMeKey);
      localStorage.removeItem(AUTH_CONFIG.userDataKey);
      localStorage.removeItem(AUTH_CONFIG.tokenKey + '_expiry');
      
      return null;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem(AUTH_CONFIG.tokenKey);
      const rememberMe = localStorage.getItem(AUTH_CONFIG.rememberMeKey) === 'true';
      const userDataStr = localStorage.getItem(AUTH_CONFIG.userDataKey);
      const expiryStr = localStorage.getItem(AUTH_CONFIG.tokenKey + '_expiry');
      
      if (!token) {
        throw new Error('No token found');
      }

      // Check if remember me session has expired
      if (rememberMe && expiryStr) {
        const expiryTime = parseInt(expiryStr, 10);
        if (Date.now() > expiryTime) {
          // Session expired, clear everything
          localStorage.removeItem(AUTH_CONFIG.tokenKey);
          localStorage.removeItem(AUTH_CONFIG.refreshTokenKey);
          localStorage.removeItem(AUTH_CONFIG.rememberMeKey);
          localStorage.removeItem(AUTH_CONFIG.userDataKey);
          localStorage.removeItem(AUTH_CONFIG.tokenKey + '_expiry');
          throw new Error('Session expired');
        }
      }
      
      // Restore user data if available
      if (userDataStr) {
        try {
          const userData = JSON.parse(userDataStr);
          return {
            token,
            user: userData,
            rememberMe,
          };
        } catch (parseError) {
          console.error('Failed to parse user data:', parseError);
          throw new Error('Invalid session data');
        }
      }
      
      // No user data available
      throw new Error('No user data found');
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  loginTime: null,
};

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.loginTime = new Date().toISOString();
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.error = action.payload || 'Login failed';
        state.user = null;
        state.token = null;
        state.refreshToken = null;
      })
      // Logout
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.loginTime = null;
        state.error = null;
      })
      .addCase(logout.rejected, (state) => {
        state.isLoading = false;
        // Even if logout fails, clear the state
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.loginTime = null;
      })
      // Check Auth
      .addCase(checkAuth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        // Restore user data if available (from remember me)
        if (action.payload.user) {
          state.user = action.payload.user;
          state.loginTime = new Date().toISOString();
        }
      })
      .addCase(checkAuth.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
      });
  },
});

// Actions
export const { clearError, updateUser } = authSlice.actions;

// Selectors
export const selectAuth = (state) => state.auth;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectCurrentUser = (state) => state.auth.user;
export const selectAuthLoading = (state) => state.auth.isLoading;
export const selectAuthError = (state) => state.auth.error;

export default authSlice.reducer;
